import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { CreditApplicationStatus } from 'shared/models/cash'

import { User } from '.'
import { Card, parseCard } from './card'
import { ClickWrap, parseClickWrap } from './docusign'
import { parsePlaidAccount, PlaidAccount } from './plaid'
import { BankAccount, Status } from './stripe'

export enum CashBalanceType {
  AVAILABLE_BALANCE = 'AVAILABLE_BALANCE',
  CARD_PENDING_CHARGES = 'CARD_PENDING_CHARGES',
}

export interface CashBalance {
  amount: string
  type: CashBalanceType
}

export interface CreditApplication {
  id: number
  amount: string
  status: CreditApplicationStatus
  statusReason: string
  originalStatus: CreditApplicationStatus
  hasBankDocuments: boolean
  dateApproved: string
  dateExpiry: string
  canBeAccepted: boolean
  notes: string
  createdBy: string
  publicRejectedReason: string
  teamBankAccountHasChanged?: boolean
  numUploadedDocuments?: number
  plaidBankAccount?: PlaidAccount
  docusignDocuments?: ClickWrap[]
}

export interface CashSummary {
  account_balances: CashBalance[]
  num_cards_not_cancelled: number
  payouts_enabled: boolean
  profile_status?: Status
  team: string
  team_card_user_object: User
  bank_account_status?: Status
  bank_account?: BankAccount
  cards?: Card[]
  lastLinkedPlaidAccount?: PlaidAccount
  lastVerifiedPlaidAccount?: PlaidAccount
  ineligibleCreditStates: string[]
  ineligibleSolePropCreditStates: string[]
  creditApplication?: CreditApplication
}

export interface CashState {
  summary: CashSummary
  cards: Card[]
}

// Api
export function getCashSummary(): Promise<CashSummary> {
  const url = '/api/v1/cash/summary/'
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return {
        account_balances: response.data.account_balances,
        bank_account: response.data.bank_account,
        bank_account_status: response.data.bank_account_status,
        num_cards_not_cancelled: response.data.num_cards_not_cancelled,
        payouts_enabled: response.data.payouts_enabled,
        profile_status: response.data.profile_status,
        team: response.data.team,
        team_card_user: response.data.team_card_user,
        team_card_user_object: response.data.team_card_user_object,
        cards: response.data.cards.map(parseCard),
        lastLinkedPlaidAccount: response.data.last_linked_plaid_account
          ? parsePlaidAccount(response.data.last_linked_plaid_account)
          : undefined,
        lastVerifiedPlaidAccount: response.data.last_verified_plaid_account
          ? parsePlaidAccount(response.data.last_verified_plaid_account)
          : undefined,
        ineligibleCreditStates: response.data.ineligible_credit_states || [],
        ineligibleSolePropCreditStates: response.data.ineligible_sole_prop_credit_states || [],
        creditApplication: response.data.credit_application
          ? {
              ...response.data.credit_application,
              createdBy: response.data.credit_application.created_by,
              dateApproved: response.data.credit_application.date_approved,
              dateExpiry: response.data.credit_application.date_expiry,
              canBeAccepted: response.data.credit_application.can_be_accepted,
              hasBankDocuments: response.data.credit_application.has_bank_documents,
              originalStatus: response.data.credit_application.original_status,
              docusignDocuments: response.data.credit_application.docusign_documents
                ? response.data.credit_application.docusign_documents.map(parseClickWrap)
                : undefined,
            }
          : undefined,
      }
    }
    throw response
  })
}

export { CreditApplicationStatus }
